import React, { useCallback, useContext, useEffect, useState } from 'react';

import { Skeleton } from '@mui/material';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import CollaboratorsTable from './sections/CollaboratorsTable/CollaboratorsTable';
import CollaboratorsToAddTable from './sections/CollaboratorsToAddTable/CollaboratorsToAddTable';
import CollaboratorsUnsubscriptionsTable from './sections/CollaboratorsUnsubscriptionsTable/CollaboratorsUnsubscriptionsTable';
import SubscribeSuccessModal from './sections/SubscribeSuccessModal/SubscribeSuccessModal';
import { ReactComponent as HappyBuddy } from '../../assets/icons/buddy-happy-open-eyes.svg';
import { ReactComponent as OutIcon } from '../../assets/icons/v3/out.svg';
import BdsActivityPill from '../../shared/components/v3/BdsActivityPill/BdsActivityPill';
import BdsSegment from '../../shared/components/v3/BdsSegment/BdsSegment';
import BdsButton from '../../shared/components/v3/Buttons/BdsButton/BdsButton';
import TertiaryHero from '../../shared/components/v3/Heros/TertiaryHero/TertiaryHero';
import BdsModal from '../../shared/components/v3/Modals/BdsModal/BdsModal';
import Toast from '../../shared/components/v3/Toast/Toast';
import { MANAGE_COLLABORATORS_ROUTE } from '../../shared/constants/auth';
import { COLLABORATORS_TABS } from '../../shared/constants/collaborators';
import { DOWNLOAD_ICON, USERS_GROUP_ICON } from '../../shared/constants/images';
import {
  CLICK_ACTION,
  INVITE_COLLABORATORS_SUCCESS,
  INVITE_FAMILY_SUCCESS,
  MANAGE_COLLABORATORS,
  REQUEST_COLLABORATOR_LIST
} from '../../shared/constants/tagManager';
import { DELETE_COLLABORATOR_SUCCESS_TOAST, DOWNLOAD_MEMBERS_ERROR_TOAST } from '../../shared/constants/toast';
import { SessionContext } from '../../shared/context/auth/authContext';
import { CompanyContext } from '../../shared/context/company/companyContext';
import useQueryParams from '../../shared/hooks/useQueryParams/useQueryParams';
import { ICollaboratorInsightsData } from '../../shared/interfaces/businessData';
import { ICollaboratorQueryParams } from '../../shared/interfaces/collaborators';
import { CollaboratorsCompanies } from '../../shared/services/api/collaborators/collaborators';
import TagManagerService from '../../shared/services/gtm/tagManagerService';
import { TRANSLATION_COLLABORATORS } from '../../shared/translations/collaborators';
import { getFromLocalStorage, toLocalStorage } from '../../shared/utils/utils';

import './Collaborators.scss';

const Collaborators = () => {
  const [insightsData, setInsightsData] = useState<ICollaboratorInsightsData>({
    actives: 0,
    lastWeekInsight: 0
  });
  const [isLoadingInsights, setIsLoadingInsights] = useState(true);
  const [errorInsights, setErrorInsights] = useState(false);
  const [tabIndex, setTabIndex] = useState(0);
  const [loadingMembersList, setLoadingMembersList] = useState(false);
  const [membersListModal, setMembersListModal] = useState(false);
  const [isErrorToastOpen, setIsErrorToastOpen] = useState(false);
  const [deleteToastOpen, setDeleteToastOpen] = useState(false);
  const [addCollabsData, setAddCollabsData] = useState({
    openModal: false,
    emails: [],
    startDate: '',
    type: '',
    method: ''
  });
  const [tabsHero, setTabsHero] = useState(COLLABORATORS_TABS);
  const [count, setCount] = useState<number>(0);
  const [addDependentsData, setAddDependentsData] = useState({
    openModal: false,
    amount: 0
  });

  const { type } = useQueryParams<ICollaboratorQueryParams>();
  const { companyData, company } = useContext(CompanyContext);
  const { holding } = useContext(CompanyContext);
  const { email } = useContext(SessionContext);
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    setTabsHero(holding.hasMergeIntegration ? COLLABORATORS_TABS : COLLABORATORS_TABS.slice(0, 2));
  }, [holding.hasMergeIntegration, holding]);

  const tabsItems = tabsHero.map((tab) => t(tab.title));

  const handleCloseMembersListModal = () => {
    setMembersListModal(false);
  };

  const downloadMemberList = async () => {
    try {
      setLoadingMembersList(true);
      await CollaboratorsCompanies.downloadMembersList(company.id);
      TagManagerService.pushEvent(CLICK_ACTION, REQUEST_COLLABORATOR_LIST);
      setMembersListModal(true);
    } catch {
      setIsErrorToastOpen(true);
    } finally {
      setLoadingMembersList(false);
    }
  };

  const handleCloseToast = (_: React.SyntheticEvent | React.MouseEvent, reason?: string) => {
    if (reason !== 'clickaway') {
      setIsErrorToastOpen(false);
      setDeleteToastOpen(false);
    }
  };

  const getActiveCollaboratorsInsights = async () => {
    setIsLoadingInsights(true);
    try {
      const data = await CollaboratorsCompanies.getActivesInsights(companyData.id);
      setInsightsData(data);
    } catch {
      setErrorInsights(true);
    } finally {
      setIsLoadingInsights(false);
    }
  };

  const onTabChange = (index: number) => {
    setTabIndex(index);
    window.history.pushState({}, '', `?type=${COLLABORATORS_TABS[index].type}`);
  };

  const renderSection = useCallback(() => {
    switch (tabIndex) {
      case 0:
        return <CollaboratorsTable />;
      case 1:
        return <CollaboratorsUnsubscriptionsTable />;
      case 2:
        return <CollaboratorsToAddTable />;
      default:
        return <></>;
    }
  }, [tabIndex]);

  const goToManageCollaborators = () => {
    navigate(MANAGE_COLLABORATORS_ROUTE);
    TagManagerService.pushEvent(CLICK_ACTION, MANAGE_COLLABORATORS);
  };

  const descriptionContent = () => {
    if (isLoadingInsights) {
      return <Skeleton className="collaborators-description-skeleton" variant="rectangular" />;
    }
    if (errorInsights || insightsData.actives === 0) {
      return t(TRANSLATION_COLLABORATORS.hero.description, { users: insightsData.actives });
    }
    return (
      <div className="collaborators-hero-description">
        {t(TRANSLATION_COLLABORATORS.hero.description, { users: insightsData.actives })}
        <BdsActivityPill
          variant="inverse"
          pictogram={{ component: OutIcon }}
          label={
            <div className="collaborators-hero-label">
              {t(TRANSLATION_COLLABORATORS.hero.labelActivityPill, {
                metric: insightsData.lastWeekInsight + '%'
              }).toUpperCase()}
            </div>
          }
        />
      </div>
    );
  };

  useEffect(() => {
    if (companyData?.id) {
      getActiveCollaboratorsInsights();
    }
  }, [companyData]);

  useEffect(() => {
    if (type) {
      const tabIndex = COLLABORATORS_TABS.findIndex((tab) => tab.type === type);
      setTabIndex(tabIndex);
    }
  }, [type]);

  useEffect(() => {
    const status = getFromLocalStorage('dependentsAdded');

    if (status) {
      const parsedData = JSON.parse(status);
      toLocalStorage('dependentsAdded', JSON.stringify({ openModal: false, amount: 0 }));
      setAddDependentsData(parsedData);
    }
  }, []);

  useEffect(() => {
    const handleSubscriptionStatus = (key: string, count: number) => {
      const status = getFromLocalStorage(key);

      if (key === 'subscriptionDeleted') {
        if (status === 'true, true' || status === 'true, false') {
          toLocalStorage(key, 'false');
          setCount(count);
          setDeleteToastOpen(true);
        }
      } else if (key === 'subscriptionAdded') {
        if (status) {
          const parsedData = JSON.parse(status);
          toLocalStorage(key, JSON.stringify({ openModal: false, emails: [], startDate: '', type: '', method: '' }));
          setAddCollabsData(parsedData);
        }
      }
    };

    handleSubscriptionStatus('subscriptionDeleted', 2);
    handleSubscriptionStatus('subscriptionAdded', 2);
  }, []);

  const onModalClick = () => {
    TagManagerService.pushEvent(CLICK_ACTION, INVITE_COLLABORATORS_SUCCESS, { method: addCollabsData.method });
    setAddCollabsData({ openModal: false, emails: [], startDate: '', type: '', method: '' });
  };

  const onDependentsModalClick = () => {
    TagManagerService.pushEvent(CLICK_ACTION, INVITE_FAMILY_SUCCESS);
    setAddDependentsData({ openModal: false, amount: 0 });
  };

  return (
    <div className="collaborators-container">
      <TertiaryHero
        title={t(TRANSLATION_COLLABORATORS.hero.title)}
        description={descriptionContent()}
        color="surface-container-quaternary-light"
        bottomContent={
          <div className="collaborators-hero-bottom-content">
            <BdsSegment items={tabsItems} activeIndex={tabIndex} action={onTabChange} />
            <div className="collaborators-hero-button">
              <BdsButton
                action={downloadMemberList}
                variant="secondary"
                size="m"
                iconLeft={DOWNLOAD_ICON}
                loading={loadingMembersList}
              >
                {t(TRANSLATION_COLLABORATORS.hero.buttonSecondary)}
              </BdsButton>
              <BdsButton action={goToManageCollaborators} variant="primary" size="m" iconLeft={USERS_GROUP_ICON}>
                {t(TRANSLATION_COLLABORATORS.hero.buttonPrimary)}
              </BdsButton>
            </div>
          </div>
        }
      />
      <div className="collaborators-table-container">{renderSection()}</div>
      <BdsModal
        open={membersListModal}
        title={t(TRANSLATION_COLLABORATORS.modal.sendMembersEmailModal.title)}
        description={
          <Trans
            i18nKey={TRANSLATION_COLLABORATORS.modal.sendMembersEmailModal.description}
            values={{ user_email: email }}
            components={{
              b: <b></b>
            }}
          />
        }
        icon={<HappyBuddy />}
        primaryAction={{
          label: t(TRANSLATION_COLLABORATORS.modal.sendMembersEmailModal.button),
          onClick: handleCloseMembersListModal
        }}
      />
      {addCollabsData.openModal && (
        <SubscribeSuccessModal open={addCollabsData.openModal} onClose={onModalClick} addCollabsData={addCollabsData} />
      )}
      <BdsModal
        open={addDependentsData.openModal}
        title={t(TRANSLATION_COLLABORATORS.modal.addDependents.title, { count: addDependentsData.amount })}
        description={t(TRANSLATION_COLLABORATORS.modal.addDependents.description)}
        icon={<HappyBuddy />}
        primaryAction={{
          label: t(TRANSLATION_COLLABORATORS.modal.addDependents.button),
          onClick: onDependentsModalClick
        }}
      />
      <Toast
        title={t(DOWNLOAD_MEMBERS_ERROR_TOAST.title)}
        description={t(DOWNLOAD_MEMBERS_ERROR_TOAST.description)}
        open={isErrorToastOpen}
        button={DOWNLOAD_MEMBERS_ERROR_TOAST.button}
        buttonText={t(DOWNLOAD_MEMBERS_ERROR_TOAST.buttonText)}
        buttonAction={handleCloseToast}
        handleClose={handleCloseToast}
        icon={DOWNLOAD_MEMBERS_ERROR_TOAST.icon}
      />
      <Toast
        title={t(DELETE_COLLABORATOR_SUCCESS_TOAST.title, { count: count })}
        description={t(DELETE_COLLABORATOR_SUCCESS_TOAST.description, { count: count })}
        open={deleteToastOpen}
        button={DELETE_COLLABORATOR_SUCCESS_TOAST.button}
        buttonText={t(DELETE_COLLABORATOR_SUCCESS_TOAST.buttonText)}
        buttonAction={handleCloseToast}
        handleClose={handleCloseToast}
        icon={DELETE_COLLABORATOR_SUCCESS_TOAST.icon}
      />
    </div>
  );
};

export default Collaborators;
